/* eslint-disable max-lines, max-lines-per-function */
import api from "common/utils/api";
import { waitForElementInViewport } from "common/utils/ui/visibility";
import animateRouteOnMap, {
  MARKER_BLUE,
  MARKER_WHITE,
  MARKER_SHADOW,
  getSvgMarkupFromTemplate,
  setMarkerSvgAttrs,
  createMarkerBaseEl
} from "./speed-map-route";

const MARKER_SIZE_PX = 56;
const MARKER_BORDER_PX = 4;

const HOME_MARKER_SVG_TEMPLATE_SELECTOR = ".js-benefits-switcher-home-marker-svg";
const WAREHOUSE_MARKER_SVG_TEMPLATE_SELECTOR = ".js-benefits-switcher-warehouse-marker-svg";

const SPEED_BENEFITS_MAP_STATE_PROP = "__speedBenefitsMapState";

const MAP_STYLE_COLORS = {
  geometry: "#f0f0f0",
  labelStroke: "#ffffff",
  labelFill: "#5a5a5a",
  water: "#dce4ec",
  road: "#fafafa",
  roadStroke: "#e8e8e8",
  landcover: "#ebebeb"
};

const GRAYSCALE_MAP_STYLES = [
  {
    featureType: "all",
    elementType: "geometry",
    stylers: [{ color: MAP_STYLE_COLORS.geometry }]
  },
  {
    featureType: "all",
    elementType: "labels.text.stroke",
    stylers: [{ color: MAP_STYLE_COLORS.labelStroke }]
  },
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{ color: MAP_STYLE_COLORS.labelFill }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: MAP_STYLE_COLORS.water }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: MAP_STYLE_COLORS.road }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: MAP_STYLE_COLORS.roadStroke }]
  },
  {
    featureType: "landscape.natural.landcover",
    elementType: "geometry",
    stylers: [{ color: MAP_STYLE_COLORS.landcover }]
  },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] }
];

let googleMapsLibPromise = null;
let DomMarkerOverlayClass = null;

function isGoogleMapsLoaded() {
  return Boolean(window.google?.maps?.importLibrary);
}

function createHomeMarkerEl() {
  const $el = createMarkerBaseEl({
    testId: "benefits-switcher-home-marker",
    sizePx: MARKER_SIZE_PX,
    borderPx: MARKER_BORDER_PX,
    background: MARKER_BLUE,
    borderColor: MARKER_WHITE,
    shadow: MARKER_SHADOW
  });
  $el.innerHTML = getSvgMarkupFromTemplate(HOME_MARKER_SVG_TEMPLATE_SELECTOR) || "";
  setMarkerSvgAttrs($el.querySelector("svg"), { width: 26, height: 26, color: MARKER_WHITE });
  return $el;
}

function createWarehouseMarkerEl() {
  const $el = createMarkerBaseEl({
    testId: "benefits-switcher-warehouse-marker",
    sizePx: MARKER_SIZE_PX,
    borderPx: MARKER_BORDER_PX,
    background: MARKER_BLUE,
    borderColor: MARKER_WHITE,
    shadow: MARKER_SHADOW
  });
  $el.innerHTML = getSvgMarkupFromTemplate(WAREHOUSE_MARKER_SVG_TEMPLATE_SELECTOR) || "";
  setMarkerSvgAttrs($el.querySelector("svg"), { width: 32, height: 25 });
  return $el;
}

async function loadGoogleMaps() {
  const mapsPromise = window.google.maps.importLibrary("maps");
  const routesPromise = window.google.maps.importLibrary("routes");
  const [{ Map }] = await Promise.all([mapsPromise, routesPromise]);
  const overlayModule = await import("./dom-marker-overlay-class");
  DomMarkerOverlayClass = overlayModule.default;
  return { Map };
}

async function loadGoogleMapsOnce() {
  if (!googleMapsLibPromise) {
    googleMapsLibPromise = loadGoogleMaps().catch(error => {
      googleMapsLibPromise = null;
      throw error;
    });
  }
  return googleMapsLibPromise;
}

function createMap(Map, $mapEl, { center }) {
  return new Map($mapEl, {
    center,
    zoom: 4,
    styles: GRAYSCALE_MAP_STYLES,
    disableDefaultUI: true,
    clickableIcons: false,
    keyboardShortcuts: false,
    gestureHandling: "none"
  });
}

function getDomMarkerOverlayClass() {
  return DomMarkerOverlayClass || null;
}

function createDomMarkerOverlay(map, { position, contentEl, title }) {
  const OverlayClass = getDomMarkerOverlayClass();
  if (!OverlayClass) return null;

  const $wrapper = document.createElement("div");
  $wrapper.style.position = "absolute";
  $wrapper.style.cursor = "default";
  $wrapper.setAttribute("title", title || "");
  $wrapper.appendChild(contentEl);

  const overlay = new OverlayClass(position, $wrapper);
  overlay.setMap(map);
  Object.defineProperty(overlay, "position", {
    get() {
      return this.markerPosition;
    },
    set(value) {
      this.markerPosition = value;
      this.draw();
    },
    configurable: true
  });
  return overlay;
}

function addWarehouseMarker(map, warehouseLocation) {
  return createDomMarkerOverlay(map, {
    position: warehouseLocation,
    contentEl: createWarehouseMarkerEl(),
    title: "Jiffy warehouse"
  });
}

function addHomeMarker(map, userLocation) {
  return createDomMarkerOverlay(map, {
    position: userLocation,
    contentEl: createHomeMarkerEl(),
    title: "Your location"
  });
}

function fitMapToLocations(map, warehouseLocation, userLocation) {
  const Bounds = window.google?.maps?.LatLngBounds;
  if (typeof Bounds !== "function") {
    map.setCenter(userLocation);
    map.setZoom(8);
    return;
  }

  const bounds = new Bounds();
  bounds.extend(warehouseLocation);
  bounds.extend(userLocation);
  map.fitBounds(bounds, 80);
}

async function fetchWarehouseAndDeliveryLocations(userLocation) {
  try {
    const payload = userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : {};
    const data = await api.made.warehouseLocations.getClosest(payload);

    const warehouseLocation = data.warehouse_location;
    const deliveryDestination = data.delivery_destination;

    const hasWarehouse = warehouseLocation?.lat != null && warehouseLocation?.lng != null;
    const hasDelivery = deliveryDestination?.lat != null && deliveryDestination?.lng != null;

    if (!hasWarehouse || !hasDelivery) {
      return null;
    }

    return {
      warehouseLocation: { lat: warehouseLocation.lat, lng: warehouseLocation.lng },
      deliveryDestination: { lat: deliveryDestination.lat, lng: deliveryDestination.lng }
    };
  } catch {
    return null;
  }
}

async function renderSpeedBenefitsMap({ $el, Map, userLocation }) {
  if (!$el) return false;

  const $mapEl = $el;

  // Shared state lives on the element so initial render and later refreshes
  // always coordinate through the same object.
  if (!$mapEl[SPEED_BENEFITS_MAP_STATE_PROP]) {
    $mapEl[SPEED_BENEFITS_MAP_STATE_PROP] = {
      map: null,
      warehouseMarker: null,
      homeMarker: null,
      routeCleanup: null,
      routeToken: null,
      locationsKey: null,
      fetchSeq: 0
    };
  }
  const state = $mapEl[SPEED_BENEFITS_MAP_STATE_PROP];

  // Bump fetchSeq so a stale fetch (from a superseded render) is discarded.
  const myFetchSeq = state.fetchSeq + 1;
  state.fetchSeq = myFetchSeq;

  const locations = await fetchWarehouseAndDeliveryLocations(userLocation);
  if (state.fetchSeq !== myFetchSeq) return false;
  if (!locations) return false;

  const { warehouseLocation, deliveryDestination } = locations;
  const locationsKey = `${warehouseLocation.lat},${warehouseLocation.lng}|${deliveryDestination.lat},${deliveryDestination.lng}`;

  const needsInitialMap = !state.map || !state.warehouseMarker || !state.homeMarker;
  const didLocationsChange = state.locationsKey !== locationsKey;

  // Already initialised with the same locations — preserve in-flight animation.
  if (!needsInitialMap && !didLocationsChange) return true;

  // -- Map + markers: render eagerly (no viewport gate). --

  if (needsInitialMap) {
    state.routeCleanup?.();
    state.routeCleanup = null;
    state.warehouseMarker?.setMap?.(null);
    state.homeMarker?.setMap?.(null);
    $mapEl.replaceChildren();

    state.map = createMap(Map, $mapEl, { center: deliveryDestination });
    state.warehouseMarker = addWarehouseMarker(state.map, warehouseLocation);
    state.homeMarker = addHomeMarker(state.map, deliveryDestination);
    state.locationsKey = locationsKey;
  } else if (didLocationsChange) {
    state.routeCleanup?.();
    state.routeCleanup = null;

    if (state.warehouseMarker) state.warehouseMarker.position = warehouseLocation;
    if (state.homeMarker) state.homeMarker.position = deliveryDestination;
    state.locationsKey = locationsKey;
  }

  fitMapToLocations(state.map, warehouseLocation, deliveryDestination);

  // -- Route animation: wait until the map is in the viewport. --
  const routeToken = {};
  state.routeToken = routeToken;

  (async () => {
    const shouldAnimate = await waitForElementInViewport($mapEl);
    if (!shouldAnimate || state.routeToken !== routeToken) return;

    state.routeCleanup?.();
    state.routeCleanup = null;

    const drivingRoute = await animateRouteOnMap({
      map: state.map,
      start: warehouseLocation,
      end: deliveryDestination,
      routeMode: "directions",
      strokeColor: MARKER_BLUE
    });

    if (state.routeToken !== routeToken) {
      drivingRoute?.cleanup?.();
      return;
    }
    if (drivingRoute?.cleanup) state.routeCleanup = drivingRoute.cleanup;
    if (drivingRoute?.didRender) return;

    // If driving directions fail, draw a straight line.
    const straightRoute = await animateRouteOnMap({
      map: state.map,
      start: warehouseLocation,
      end: deliveryDestination,
      routeMode: "straight",
      strokeColor: MARKER_BLUE
    });
    if (straightRoute?.cleanup) state.routeCleanup = straightRoute.cleanup;
  })().catch(() => {
    // no-op
  });

  return true;
}

function clearMapState($mapEl) {
  const state = $mapEl?.[SPEED_BENEFITS_MAP_STATE_PROP];
  if (!state) return;
  state.routeCleanup?.();
  state.routeCleanup = null;
  state.routeToken = null;
  state.warehouseMarker?.setMap?.(null);
  state.homeMarker?.setMap?.(null);
  state.warehouseMarker = null;
  state.homeMarker = null;
  state.map = null;
  state.locationsKey = null;
  $mapEl.replaceChildren();
}

export async function refreshSpeedBenefitsMapFromZipChange($mapEl) {
  const state = $mapEl[SPEED_BENEFITS_MAP_STATE_PROP];
  if (!state) return;
  if (!isGoogleMapsLoaded()) return;

  const { Map } = await loadGoogleMapsOnce();
  clearMapState($mapEl);

  try {
    await renderSpeedBenefitsMap({
      $el: $mapEl,
      Map,
      userLocation: null
    });
  } catch {
    // eslint-disable-next-line no-param-reassign -- reset so initSpeedBenefitsMapInternal can run again on next tab switch
    delete $mapEl.dataset.mapInitialized;
  }
}

async function initSpeedBenefitsMapInternal($mapEl) {
  if (!$mapEl) return;
  const $el = $mapEl;
  if ($el.dataset.mapInitialized === "initial") return;

  $el.dataset.mapInitialized = "initial";

  if (!isGoogleMapsLoaded()) return;

  const { Map } = await loadGoogleMapsOnce();

  await renderSpeedBenefitsMap({
    $el,
    Map,
    userLocation: null
  });
}

export default function initSpeedBenefitsMap($mapEl) {
  initSpeedBenefitsMapInternal($mapEl).catch(() => {
    // no-op: map is purely decorative on this page
  });
}
